import { useState, useEffect } from 'react';
import { Star, Check, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { apiFetch } from '../../utils/api';

export default function TestimonialsManager() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTestimonials = () => {
    apiFetch('/testimonials?approved_only=false')
      .then(r => r.json())
      .then(data => {
        setTestimonials(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchTestimonials(); }, []);

  const handleApprove = async (id) => {
    try {
      const res = await apiFetch(`/testimonials/${id}/approve`, { method: 'PUT' });
      if (res.ok) {
        toast.success('Testimonial approved');
        fetchTestimonials();
      }
    } catch {
      toast.error('Failed to approve');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await apiFetch(`/testimonials/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Testimonial deleted');
        fetchTestimonials();
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="testimonials-manager-page">
      <div className="mb-8">
        <p className="overline text-[#6B7280] mb-2">Manage</p>
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0A1128] tracking-tight">
          Testimonials
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-24">
          <div className="w-8 h-8 border-2 border-[#0A1128] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-24 border border-[#E5E7EB]">
          <Star className="w-8 h-8 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#6B7280] text-sm">No testimonials yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((t) => (
            <div key={t.id} className={`border ${t.approved ? 'border-[#E5E7EB]' : 'border-[#C9A96E]'} p-6`} data-testid={`testimonial-admin-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-medium text-[#0A1128]">{t.name}</span>
                    <div className="flex gap-0.5">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 text-[#C9A96E] fill-[#C9A96E]" />
                      ))}
                    </div>
                    {t.approved ? (
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 uppercase tracking-widest">Approved</span>
                    ) : (
                      <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 uppercase tracking-widest">Pending</span>
                    )}
                  </div>
                  <p className="text-[#6B7280] text-sm leading-relaxed">"{t.review}"</p>
                  <p className="text-[#6B7280] text-xs mt-2">{new Date(t.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  {!t.approved && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleApprove(t.id)}
                      className="rounded-none border-green-300 text-green-600 hover:bg-green-50 text-xs"
                      data-testid={`approve-testimonial-${t.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(t.id)}
                    className="rounded-none border-red-300 text-red-600 hover:bg-red-50 text-xs"
                    data-testid={`delete-testimonial-${t.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
